import type { PhilosopherMetadata, PineconeQueryResponse } from '../types/pinecone';

// Fallback service with mock data for development/testing
export class PineconeServiceFallback {
  private mockData: Array<{
    id: string;
    score: number;
    metadata: PhilosopherMetadata;
    text: string;
  }> = [
    {
      id: 'adam-smith-1',
      score: 0.95,
      metadata: {
        philosopher: 'adam-smith',
        work_title: 'The Wealth of Nations',
        chapter: 'Book I: Chapter 1 - Of the Division of Labour',
        chunk_index: 1,
        total_chunks: 2400,
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        text: 'The greatest improvement in the productive powers of labour, and the greater part of the skill, dexterity, and judgment with which it is anywhere directed, or applied, seem to have been the effects of the division of labour. The effects of the division of labour, in the general business of society, will be more easily understood by considering in what manner it operates in some particular manufactures.'
      },
      text: 'The greatest improvement in the productive powers of labour, and the greater part of the skill, dexterity, and judgment with which it is anywhere directed, or applied, seem to have been the effects of the division of labour. The effects of the division of labour, in the general business of society, will be more easily understood by considering in what manner it operates in some particular manufactures.'
    },
    {
      id: 'adam-smith-2',
      score: 0.88,
      metadata: {
        philosopher: 'adam-smith',
        work_title: 'The Wealth of Nations',
        chapter: 'Book I: Chapter 2 - Of the Principle which gives occasion to the Division of Labour',
        chunk_index: 25,
        total_chunks: 2400,
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        text: 'This division of labour, from which so many advantages are derived, is not originally the effect of any human wisdom, which foresees and intends that general opulence to which it gives occasion. It is the necessary, though very slow and gradual consequence of a certain propensity in human nature which has in view no such extensive utility; the propensity to truck, barter and exchange one thing for another.'
      },
      text: 'This division of labour, from which so many advantages are derived, is not originally the effect of any human wisdom, which foresees and intends that general opulence to which it gives occasion. It is the necessary, though very slow and gradual consequence of a certain propensity in human nature which has in view no such extensive utility; the propensity to truck, barter and exchange one thing for another.'
    },
    {
      id: 'karl-marx-1',
      score: 0.92,
      metadata: {
        philosopher: 'karl-marx',
        work_title: 'The Communist Manifesto',
        chapter: 'Chapter 1 - Bourgeois and Proletarians',
        chunk_index: 1,
        total_chunks: 88,
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        text: 'The history of all hitherto existing society is the history of class struggles. Freeman and slave, patrician and plebeian, lord and serf, guild-master and journeyman, in a word, oppressor and oppressed, stood in constant opposition to one another, carried on an uninterrupted, now hidden, now open fight, a fight that each time ended, either in a revolutionary reconstitution of society at large, or in the common ruin of the contending classes.'
      },
      text: 'The history of all hitherto existing society is the history of class struggles. Freeman and slave, patrician and plebeian, lord and serf, guild-master and journeyman, in a word, oppressor and oppressed, stood in constant opposition to one another, carried on an uninterrupted, now hidden, now open fight, a fight that each time ended, either in a revolutionary reconstitution of society at large, or in the common ruin of the contending classes.'
    },
    {
      id: 'karl-marx-2',
      score: 0.87,
      metadata: {
        philosopher: 'karl-marx',
        work_title: 'The Communist Manifesto', 
        chapter: 'Chapter 1 - Bourgeois and Proletarians',
        chunk_index: 15,
        total_chunks: 88,
        timestamp: new Date().toISOString(),
        version: '1.0.0',
        text: 'The modern bourgeois society that has sprouted from the ruins of feudal society has not done away with class antagonisms. It has but established new classes, new conditions of oppression, new forms of struggle in place of the old ones. Our epoch, the epoch of the bourgeoisie, possesses, however, this distinct feature: it has simplified class antagonisms.'
      },
      text: 'The modern bourgeois society that has sprouted from the ruins of feudal society has not done away with class antagonisms. It has but established new classes, new conditions of oppression, new forms of struggle in place of the old ones. Our epoch, the epoch of the bourgeoisie, possesses, however, this distinct feature: it has simplified class antagonisms.'
    }
  ];

  async query(
    vector: number[],
    philosopher?: 'adam-smith' | 'karl-marx',
    topK: number = 10
  ): Promise<PineconeQueryResponse> {
    console.log('Using fallback Pinecone service with mock data');
    
    // Filter by philosopher if specified
    let filteredData = this.mockData;
    if (philosopher) {
      filteredData = this.mockData.filter(item => item.metadata.philosopher === philosopher);
    }
    
    // Return top K results with text included
    const matches = filteredData.slice(0, topK);
    
    return {
      matches: matches.map(match => ({
        id: match.id,
        score: match.score,
        metadata: match.metadata,
        text: match.text, // Include text for context
      }))
    };
  }

  async upsertVectors(): Promise<{ upsertedCount: number }> {
    console.log('Fallback service: upsert not implemented');
    return { upsertedCount: 0 };
  }

  async describeIndexStats(): Promise<{
    dimension: number;
    indexFullness: number;
    totalVectorCount: number;
  }> {
    console.log('Fallback service: returning mock stats');
    return {
      dimension: 1536,
      indexFullness: 0.1,
      totalVectorCount: this.mockData.length
    };
  }

  async deleteAll(): Promise<Record<string, never>> {
    console.log('Fallback service: delete not implemented');
    return {};
  }
}

export const pineconeServiceFallback = new PineconeServiceFallback();